import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  ShieldCheckIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import {
  fetchAitmTrustedPairs,
  addAitmTrustedPair,
  updateAitmTrustedPair,
  removeAitmTrustedPair,
  fetchTenants,
} from '../api/client';
import { useAuth } from '../hooks/useAuth';
import Pagination from './Pagination';

function formatDate(dateStr) {
  if (!dateStr) return '\u2014';
  try {
    return format(parseISO(dateStr), 'MMM d, yyyy');
  } catch {
    return dateStr;
  }
}

export default function AitmTrustedPairsManager() {
  const { user } = useAuth();
  const isAdmin = !!user?.isAdmin;

  const [rows, setRows] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterQuery, setFilterQuery] = useState('');

  // Tenant filter (client-side): 'all' or specific tenantId
  const [tenantFilter, setTenantFilter] = useState('all');

  // Add form state — wildcard checkboxes flip the corresponding text input to "*" on save
  const [formTenantId, setFormTenantId] = useState('');
  const [formHost, setFormHost] = useState('');
  const [formHostAny, setFormHostAny] = useState(false);
  const [formAppDisplayName, setFormAppDisplayName] = useState('');
  const [formAppDisplayAny, setFormAppDisplayAny] = useState(false);
  const [formAppId, setFormAppId] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(null);

  const [confirmRemove, setConfirmRemove] = useState(null); // composite key tenantId::rowKey
  const [removing, setRemoving] = useState(false);

  // Inline edit (notes + appDisplayName + appId; tenant + host are immutable identity)
  const [editingId, setEditingId] = useState(null); // composite key tenantId::rowKey
  const [editNotes, setEditNotes] = useState('');
  const [editAppDisplay, setEditAppDisplay] = useState('');
  const [editAppDisplayAny, setEditAppDisplayAny] = useState(false);
  const [editAppId, setEditAppId] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Resizable columns: Tenant, Host, App Display Name, App ID, Notes, Added By, Date, Actions
  const [colWidths, setColWidths] = useState([200, 240, 200, 200, 280, 160, 130, 100]);
  const thRefs = useRef([]);
  const tableRef = useRef(null);

  const startResize = useCallback((e, colIdx) => {
    e.preventDefault();
    const th = thRefs.current[colIdx];
    const table = tableRef.current;
    if (!th) return;
    const startX = e.clientX;
    const startWidth = th.offsetWidth;
    const startTableWidth = table ? table.offsetWidth : 0;
    let lastWidth = startWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMouseMove = (moveEvt) => {
      lastWidth = Math.max(50, startWidth + moveEvt.clientX - startX);
      th.style.width = `${lastWidth}px`;
      if (table) table.style.width = `${startTableWidth + (lastWidth - startWidth)}px`;
    };
    const onMouseUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      setColWidths(prev => { const n = [...prev]; n[colIdx] = lastWidth; return n; });
      if (table) table.style.width = '';
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [pairsData, tenantData] = await Promise.all([
        fetchAitmTrustedPairs('all'),
        fetchTenants(),
      ]);
      setRows(Array.isArray(pairsData) ? pairsData : []);
      setTenants(Array.isArray(tenantData) ? tenantData : []);
    } catch (err) {
      setError(err.message || 'Failed to load AiTM trusted pairs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Map tenantId → friendly name for display
  const tenantNameById = useMemo(() => {
    const map = {};
    tenants.forEach((t) => {
      if (t.TenantID) map[t.TenantID] = t.FriendlyName || t.TenantID;
    });
    return map;
  }, [tenants]);

  const filtered = useMemo(() => {
    let result = rows;
    if (tenantFilter !== 'all') {
      // Include rows tagged for the selected tenant AND wildcard rows that apply to all tenants
      result = result.filter((r) => r.tenantId === tenantFilter || r.tenantId === '*');
    }
    if (filterQuery.trim()) {
      const q = filterQuery.toLowerCase();
      result = result.filter(
        (r) =>
          (r.host && r.host.toLowerCase().includes(q)) ||
          (r.appDisplayName && r.appDisplayName.toLowerCase().includes(q)) ||
          (r.appId && r.appId.toLowerCase().includes(q)) ||
          (r.notes && r.notes.toLowerCase().includes(q)) ||
          (r.addedBy && r.addedBy.toLowerCase().includes(q))
      );
    }
    return result;
  }, [rows, filterQuery, tenantFilter]);

  useEffect(() => { setCurrentPage(1); }, [filterQuery, tenantFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIdx = (safeCurrentPage - 1) * pageSize;
  const paged = filtered.slice(startIdx, startIdx + pageSize);

  const handleAdd = async (e) => {
    e.preventDefault();
    const effectiveHost = formHostAny ? '*' : formHost.trim().toLowerCase();
    const effectiveAppDisplay = formAppDisplayAny ? '*' : formAppDisplayName.trim();
    const effectiveAppId = formAppId.trim();

    if (!formTenantId) return;
    if (!effectiveHost) return;
    if (!effectiveAppDisplay && !effectiveAppId) {
      setAddError('At least one of App Display Name or App ID is required.');
      return;
    }
    // Client-side guard: refuse all-three-wildcards (server also enforces)
    if (formTenantId === '*' && effectiveHost === '*' && (effectiveAppDisplay === '*' || effectiveAppId === '*')) {
      setAddError('Cannot create a row where tenant, host, and app are all wildcards (*) — that would suppress every alert.');
      return;
    }

    setAdding(true);
    setAddError(null);
    try {
      const created = await addAitmTrustedPair({
        tenantId: formTenantId,
        host: effectiveHost,
        appDisplayName: effectiveAppDisplay,
        appId: effectiveAppId,
        notes: formNotes.trim(),
      });
      setRows((prev) => [created, ...prev]);
      setFormTenantId('');
      setFormHost('');
      setFormHostAny(false);
      setFormAppDisplayName('');
      setFormAppDisplayAny(false);
      setFormAppId('');
      setFormNotes('');
    } catch (err) {
      setAddError(err.message || 'Failed to add pair');
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (row) => {
    setRemoving(true);
    try {
      await removeAitmTrustedPair(row.tenantId, row.rowKey);
      setRows((prev) => prev.filter((item) => item.rowKey !== row.rowKey || item.tenantId !== row.tenantId));
      setConfirmRemove(null);
    } catch (err) {
      setError(err.message || 'Failed to remove pair');
    } finally {
      setRemoving(false);
    }
  };

  const startEdit = (row) => {
    setEditError(null);
    setConfirmRemove(null);
    setEditingId(`${row.tenantId}::${row.rowKey}`);
    setEditNotes(row.notes || '');
    const isAppWildcard = row.appDisplayName === '*';
    setEditAppDisplayAny(isAppWildcard);
    setEditAppDisplay(isAppWildcard ? '' : (row.appDisplayName || ''));
    setEditAppId(row.appId || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditNotes('');
    setEditAppDisplay('');
    setEditAppDisplayAny(false);
    setEditAppId('');
    setEditError(null);
  };

  const handleSaveEdit = async (row) => {
    const effectiveAppDisplay = editAppDisplayAny ? '*' : editAppDisplay.trim();
    const effectiveAppId = editAppId.trim();
    if (!effectiveAppDisplay && !effectiveAppId) {
      setEditError('At least one of App Display Name or App ID is required.');
      return;
    }
    // Client-side guard: refuse all-three-wildcards (tenant + host are immutable on edit, so check them on the row itself)
    if (row.tenantId === '*' && row.host === '*' && (effectiveAppDisplay === '*' || effectiveAppId === '*')) {
      setEditError('Cannot save a row where tenant, host, and app are all wildcards (*) — that would suppress every alert.');
      return;
    }
    setSavingEdit(true);
    setEditError(null);
    try {
      const updated = await updateAitmTrustedPair(row.tenantId, row.rowKey, {
        notes: editNotes.trim(),
        appDisplayName: effectiveAppDisplay,
        appId: effectiveAppId,
      });
      setRows((prev) => prev.map((item) => (
        item.tenantId === row.tenantId && item.rowKey === row.rowKey ? { ...item, ...updated } : item
      )));
      cancelEdit();
    } catch (err) {
      setEditError(err.message || 'Failed to save changes');
    } finally {
      setSavingEdit(false);
    }
  };

  const sortedTenants = useMemo(
    () => [...tenants].sort((a, b) => (a.FriendlyName || '').localeCompare(b.FriendlyName || '')),
    [tenants]
  );

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <ShieldCheckIcon className="h-7 w-7 text-blue-500" />
          <h1 className="text-2xl font-bold text-gray-100">AiTM Trusted Pairs</h1>
        </div>
        <p className="text-sm text-gray-500 max-w-3xl">
          Per-tenant <span className="font-mono text-gray-400">(click_host, sign-in app)</span> overrides for <span className="font-mono text-gray-400">rule_aitm_url_click_to_new_ip_signin</span>. Both the click host AND the matched sign-in's app must equal the row before suppression fires. Tenant, host, and app each accept the wildcard <span className="font-mono text-amber-300">*</span> for cross-tenant rules, "any host", or "any app" patterns — but you cannot wildcard all three at once.
        </p>
      </div>

      {/* Add Form (admin only) */}
      {isAdmin ? (
        <div className="card mb-6">
          <h2 className="text-sm font-semibold text-gray-300 mb-4">Add Trusted Pair</h2>
          <form onSubmit={handleAdd} className="space-y-3">
            {/* Single row, columns match table below: Tenant | Host | App Display Name | App ID | Notes | Add */}
            <div className="flex flex-col lg:flex-row gap-3 lg:items-start">
              {/* Tenant */}
              <div className="flex-1 min-w-0">
                <label className="block text-xs text-gray-500 mb-1">Tenant</label>
                <select
                  value={formTenantId}
                  onChange={(e) => setFormTenantId(e.target.value)}
                  className="input-field h-10 py-0"
                  required
                >
                  <option value="">— Tenant —</option>
                  <option value="*">All tenants (*)</option>
                  {sortedTenants.map((t) => (
                    <option key={t.TenantID} value={t.TenantID}>{t.FriendlyName || t.TenantID}</option>
                  ))}
                </select>
                {/* Spacer to match the height of "Any host (*)" / "Any app (*)" checkboxes in sibling columns */}
                <div className="mt-1 h-[22px]" aria-hidden="true" />
              </div>

              {/* Host */}
              <div className="flex-1 min-w-0">
                <label className="block text-xs text-gray-500 mb-1">Host</label>
                <input
                  type="text"
                  placeholder="e.g. constyn.sellersdorsey.com"
                  value={formHostAny ? '*' : formHost}
                  onChange={(e) => setFormHost(e.target.value)}
                  disabled={formHostAny}
                  className={`input-field h-10 py-0 font-mono text-xs ${formHostAny ? 'opacity-60 cursor-not-allowed' : ''}`}
                  required={!formHostAny}
                />
                <label className="mt-1 flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formHostAny}
                    onChange={(e) => setFormHostAny(e.target.checked)}
                    className="rounded border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500"
                  />
                  Any host (*)
                </label>
              </div>

              {/* App match — Display Name and/or App ID; at least one required */}
              <div className="flex-[2] min-w-0">
                <label className="block text-xs text-gray-500 mb-1">
                  App match <span className="text-amber-300">*</span>
                  <span className="text-gray-600 font-normal"> — fill App Display Name, App ID, or both</span>
                </label>
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <input
                      type="text"
                      placeholder="App Display Name (e.g. Constyn SSO)"
                      value={formAppDisplayAny ? '*' : formAppDisplayName}
                      onChange={(e) => setFormAppDisplayName(e.target.value)}
                      disabled={formAppDisplayAny}
                      className={`input-field h-10 py-0 w-full ${formAppDisplayAny ? 'opacity-60 cursor-not-allowed' : ''}`}
                    />
                    <label className="mt-1 flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formAppDisplayAny}
                        onChange={(e) => setFormAppDisplayAny(e.target.checked)}
                        className="rounded border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500"
                      />
                      Any app (*)
                    </label>
                  </div>
                  <div className="h-10 flex items-center text-[10px] font-semibold text-gray-600 uppercase tracking-wider px-1 whitespace-nowrap">and / or</div>
                  <div className="flex-1 min-w-0">
                    <input
                      type="text"
                      placeholder="App ID (GUID)"
                      value={formAppId}
                      onChange={(e) => setFormAppId(e.target.value)}
                      className="input-field h-10 py-0 font-mono text-xs w-full"
                    />
                    {/* Spacer to match "Any app (*)" checkbox space under the sibling input */}
                    <div className="mt-1 h-[22px]" aria-hidden="true" />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="flex-1 min-w-0">
                <label className="block text-xs text-gray-500 mb-1">Notes</label>
                <input
                  type="text"
                  placeholder="justification — required for wildcards"
                  value={formNotes}
                  onChange={(e) => setFormNotes(e.target.value)}
                  className="input-field h-10 py-0"
                />
                {/* Spacer to match the height of checkbox rows in sibling columns */}
                <div className="mt-1 h-[22px]" aria-hidden="true" />
              </div>

              {/* Add button */}
              <div className="flex-shrink-0 lg:pt-5">
                <button
                  type="submit"
                  disabled={
                    adding ||
                    !formTenantId ||
                    (!formHostAny && !formHost.trim()) ||
                    (!formAppDisplayAny && !formAppDisplayName.trim() && !formAppId.trim())
                  }
                  className="btn-primary flex items-center gap-2 whitespace-nowrap"
                >
                  {adding ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <PlusIcon className="h-4 w-4" />}
                  Add Pair
                </button>
              </div>
            </div>
            {(formTenantId === '*' || formHostAny) && (
              <p className="text-xs text-amber-300/80 flex items-start gap-1.5">
                <ExclamationTriangleIcon className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                <span>Wildcard rule: applies broadly. Add a Notes justification so future admins know why.</span>
              </p>
            )}
            <p className="text-xs text-gray-600">At least one of App Display Name or App ID is required. Tenant + host + app cannot all be wildcards.</p>
          </form>
          {addError && <p className="mt-2 text-sm text-red-400">{addError}</p>}
        </div>
      ) : (
        <div className="card mb-6 flex items-center gap-3 text-sm text-gray-400">
          <LockClosedIcon className="h-4 w-4 text-gray-500" />
          <span>Read-only view — admin access is required to add or remove entries.</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="mb-4 p-4 bg-red-900/30 border border-red-700/50 rounded-lg text-red-300 text-sm flex items-center gap-2">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={load} className="ml-auto underline hover:no-underline text-red-400">Retry</button>
        </div>
      )}

      {/* Edit error banner */}
      {editError && (
        <div className="mb-4 p-3 bg-red-900/30 border border-red-700/50 rounded-lg text-red-300 text-sm flex items-center gap-2">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
          <span>{editError}</span>
        </div>
      )}

      {/* Filters and Count */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex flex-col sm:flex-row gap-3 flex-1">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <input
              type="text"
              placeholder="Filter by host, app, notes..."
              value={filterQuery}
              onChange={(e) => setFilterQuery(e.target.value)}
              className="input-field pl-9"
            />
          </div>
          <select
            value={tenantFilter}
            onChange={(e) => setTenantFilter(e.target.value)}
            className="input-field sm:w-64"
          >
            <option value="all">All tenants</option>
            {sortedTenants.map((t) => (
              <option key={t.TenantID} value={t.TenantID}>{t.FriendlyName || t.TenantID}</option>
            ))}
          </select>
        </div>
        <span className="text-xs text-gray-500">
          {filtered.length} pair{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table
            ref={tableRef}
            className="text-sm"
            style={{ tableLayout: 'fixed', width: `${colWidths.reduce((a, b) => a + b, 0)}px` }}
          >
            <thead>
              <tr className="border-b border-gray-800">
                {['Tenant', 'Host', 'App Display Name', 'App ID', 'Notes', 'Added By', 'Date', 'Actions'].map((label, colIdx) => (
                  <th
                    key={label}
                    ref={(el) => { thRefs.current[colIdx] = el; }}
                    className={`${colIdx === 7 ? 'text-center' : 'text-left'} text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap relative select-none`}
                    style={{ width: `${colWidths[colIdx]}px` }}
                  >
                    <span className="truncate">{label}</span>
                    <div
                      className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-10 flex items-stretch justify-center group"
                      onMouseDown={(e) => startResize(e, colIdx)}
                    >
                      <div className="w-px bg-gray-700/50 group-hover:bg-blue-500/70 transition-colors" />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {loading && (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      <span className="text-gray-500 text-sm">Loading trusted pairs...</span>
                    </div>
                  </td>
                </tr>
              )}

              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center">
                    <ShieldCheckIcon className="h-10 w-10 text-gray-700 mx-auto mb-3" />
                    <p className="text-gray-400 font-medium">No trusted pairs found</p>
                    <p className="text-gray-600 text-sm mt-1">
                      {filterQuery || tenantFilter !== 'all'
                        ? 'Try adjusting your filters.'
                        : isAdmin ? 'Add a pair above to get started.' : 'No entries yet.'}
                    </p>
                  </td>
                </tr>
              )}

              {!loading && paged.map((r) => {
                const compositeKey = `${r.tenantId}::${r.rowKey}`;
                const isEditing = editingId === compositeKey;
                return (
                <tr key={compositeKey} className="hover:bg-gray-800/50 transition-colors">
                  <td className="px-4 py-3 text-gray-200 truncate overflow-hidden" title={r.tenantId}>
                    {r.tenantId === '*' ? (
                      <span className="px-1.5 py-0.5 rounded bg-amber-900/30 text-amber-300 border border-amber-700/40 text-xs">All tenants (*)</span>
                    ) : (
                      tenantNameById[r.tenantId] || r.tenantId
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-300 truncate overflow-hidden" title={r.host}>
                    {r.host === '*' ? (
                      <span className="px-1.5 py-0.5 rounded bg-amber-900/30 text-amber-300 border border-amber-700/40 text-xs font-sans">Any host (*)</span>
                    ) : (
                      r.host
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-300 truncate overflow-hidden" title={r.appDisplayName || ''}>
                    {isEditing ? (
                      <div className="space-y-1">
                        <input
                          type="text"
                          value={editAppDisplayAny ? '*' : editAppDisplay}
                          onChange={(e) => setEditAppDisplay(e.target.value)}
                          disabled={editAppDisplayAny}
                          className={`input-field w-full text-xs py-1 ${editAppDisplayAny ? 'opacity-60 cursor-not-allowed' : ''}`}
                          autoFocus
                        />
                        <label className="flex items-center gap-1.5 text-[10px] text-gray-500 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editAppDisplayAny}
                            onChange={(e) => setEditAppDisplayAny(e.target.checked)}
                            className="rounded border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500"
                          />
                          Any app (*)
                        </label>
                      </div>
                    ) : r.appDisplayName === '*' ? (
                      <span className="px-1.5 py-0.5 rounded bg-amber-900/30 text-amber-300 border border-amber-700/40 text-xs">Any app (*)</span>
                    ) : (
                      r.appDisplayName || '\u2014'
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-500 truncate overflow-hidden" title={r.appId || ''}>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editAppId}
                        onChange={(e) => setEditAppId(e.target.value)}
                        className="input-field w-full text-xs py-1 font-mono"
                      />
                    ) : (
                      r.appId || '\u2014'
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-400 truncate overflow-hidden" title={r.notes || ''}>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editNotes}
                        onChange={(e) => setEditNotes(e.target.value)}
                        className="input-field w-full text-xs py-1"
                      />
                    ) : (
                      r.notes || '\u2014'
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs truncate overflow-hidden" title={r.addedBy || ''}>
                    {r.addedBy || '\u2014'}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs truncate overflow-hidden">
                    {formatDate(r.addedUtc)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {!isAdmin ? (
                      <span className="text-gray-700">{'\u2014'}</span>
                    ) : isEditing ? (
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleSaveEdit(r)}
                          disabled={savingEdit}
                          className="text-gray-600 hover:text-green-400 transition-colors p-1"
                          title="Save"
                        >
                          {savingEdit ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <CheckIcon className="h-4 w-4" />}
                        </button>
                        <button
                          onClick={cancelEdit}
                          disabled={savingEdit}
                          className="text-gray-600 hover:text-gray-300 transition-colors p-1"
                          title="Cancel"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ) : confirmRemove === compositeKey ? (
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleRemove(r)}
                          disabled={removing}
                          className="btn-danger px-2 py-1 text-xs"
                        >
                          {removing ? 'Removing...' : 'Confirm'}
                        </button>
                        <button
                          onClick={() => setConfirmRemove(null)}
                          className="btn-secondary px-2 py-1 text-xs"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => startEdit(r)}
                          className="text-gray-600 hover:text-blue-400 transition-colors p-1"
                          title="Edit notes / app fields"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setConfirmRemove(compositeKey)}
                          className="text-gray-600 hover:text-red-400 transition-colors p-1"
                          title="Remove pair"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!loading && filtered.length > 0 && (
          <Pagination
            currentPage={safeCurrentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={filtered.length}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
          />
        )}
      </div>
    </div>
  );
}
